// packages/utils/src/client/olderReleaseRecovery.ts
//
// What a page from another release does about it (`pageRelease.ts`): it reloads itself, so its next
// click works, when that loses nothing and interrupts nobody; otherwise it shows the "TourTask has
// been updated" notice and asks again each time its tab is hidden. Pure, so the rule is unit-tested;
// `useOlderReleaseRecovery` (`@tourtask/ui`) reads the facts from the page and does the reload.
//
// Nothing to lose: no form holds unsaved changes (an armed unsaved-changes guard), no dialog is open
// (it may hold a half-filled form no guard knows of) and the focus is not in a text field. Nobody to
// interrupt: the tab is hidden, or it was shown a moment ago and nobody has clicked or typed since —
// a tab brought back after a deploy finds out at once (its badges are polled on return) and reloads
// before it can be used. A page that reloaded itself for this a minute ago does not again: during a
// release switch the reload can land on the old server once more.

export interface OlderReleaseFacts {
  /** The tab is hidden: nobody is looking. */
  hidden: boolean;
  /** Milliseconds since the page was last shown: loaded, restored, or its tab brought back. */
  msSinceShown: number;
  /** A click, tap or key press since it was last shown. */
  touchedSinceShown: boolean;
  /** An unsaved-changes guard is armed: a form holds edits not saved yet. */
  unsavedChanges: boolean;
  /** A dialog is open. */
  dialogOpen: boolean;
  /** The focus is in a text field. */
  editingText: boolean;
  /** Milliseconds since this tab last reloaded itself for this; null = it has not. */
  msSinceLastSelfReload: number | null;
}

/** A page shown this recently, and untouched since, reloads before anyone can use it. */
export const JUST_SHOWN_MS = 3_000;

/** A tab that reloaded itself this recently shows the notice instead (no reload loop). */
export const SELF_RELOAD_MIN_INTERVAL_MS = 60_000;

export type OlderReleaseRecovery = 'reload' | 'notice';

export function olderReleaseRecovery(facts: OlderReleaseFacts): OlderReleaseRecovery {
  if (facts.unsavedChanges || facts.dialogOpen || facts.editingText) return 'notice';
  if (facts.msSinceLastSelfReload !== null && facts.msSinceLastSelfReload < SELF_RELOAD_MIN_INTERVAL_MS) {
    return 'notice';
  }
  if (facts.hidden) return 'reload';
  return facts.msSinceShown < JUST_SHOWN_MS && !facts.touchedSinceShown ? 'reload' : 'notice';
}
